import React, { Fragment, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import ReceiptActions from "../Common/ReceiptActions";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(value || 0));

const getPaymentRows = (record) => {
  const payments = Array.isArray(record?.payments) ? [...record.payments] : [];
  const paidTotal = Math.round(Number(record?.paidAmount || 0) * 100) / 100;
  const recordedTotal = Math.round(
    payments.reduce((sum, p) => sum + Number(p.amount || 0), 0) * 100
  ) / 100;
  const legacyAmount = Math.round((paidTotal - recordedTotal) * 100) / 100;

  if (legacyAmount > 0.005) {
    payments.unshift({
      _id: `legacy-${record._id}`,
      amount: legacyAmount,
      paymentReference: record.paymentReference,
      paymentRemarks: record.paymentRemarks,
      paymentDate: record.paidDate || record.paymentDate || record.updatedAt,
      receipt: record.receipt,
      additionalPaymentType: record.additionalPaymentType,
      additionalPaymentAmount: record.additionalPaymentAmount,
      additionalPaymentReceipt: record.additionalPaymentReceipt,
      legacy: true,
    });
  }
  return payments;
};

const statusVariant = (status) => {
  if (status === "Paid") return "success";
  if (status === "Partially Paid") return "warning";
  if (status === "Rejected") return "danger";
  return "info";
};

const OtherPurchaseOrderPaymentList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = usePersistentState("other_po_pay_search", searchFromUrl);
  const [statusFilter, setStatusFilter] = usePersistentState("other_po_pay_status", "All");

  // Payment Modal State
  const [selectedPO, setSelectedPO] = useState(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  const [additionalPaymentType, setAdditionalPaymentType] = useState("");
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState("");
  const [additionalReceiptFile, setAdditionalReceiptFile] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  // Remarks Modal State
  const [remarkPO, setRemarkPO] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // Load Other Purchase Orders
  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await api.get("/purchase-orders/other?limit=1000");
      if (res.data?.success) {
        setOrders(res.data.data || []);
      }
    } catch (error) {
      console.error("Error loading other purchase orders:", error);
      swal("Error", "Failed to load other purchase orders", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
    }
  }, [searchFromUrl]);

  // Payment Preview Calculation
  const paymentPreview = selectedPO
    ? (() => {
      const total = Math.round(Number(selectedPO.totalAmount || 0) * 100) / 100;
      const currentPaid = Math.round(Number(selectedPO.paidAmount || 0) * 100) / 100;
      const nextPaid = Math.round((currentPaid + Number(paidAmount || 0)) * 100) / 100;
      const remaining = Math.max(Math.round((total - currentPaid) * 100) / 100, 0);
      const nextRemaining = Math.max(Math.round((total - nextPaid) * 100) / 100, 0);
      const addlAmt = Number(additionalPaymentAmount || 0);
      const nextStatus =
        nextPaid >= total - 0.5 && total > 0
          ? "Paid"
          : nextPaid > 0
            ? "Partially Paid"
            : "Pending";

      return {
        total,
        currentPaid,
        nextPaid,
        remaining,
        nextRemaining,
        addlAmt,
        nextStatus,
      };
    })()
    : null;

  const openPayment = (po) => {
    const total = Math.round(Number(po.totalAmount || 0) * 100) / 100;
    const currentPaid = Math.round(Number(po.paidAmount || 0) * 100) / 100;
    const remaining = Math.max(Math.round((total - currentPaid) * 100) / 100, 0);

    setSelectedPO(po);
    setPaidAmount(remaining > 0 ? String(remaining) : "");
    setPaymentReference("");
    setPaymentRemarks("");
    setAdditionalPaymentType("");
    setAdditionalPaymentAmount("");
    setAdditionalReceiptFile(null);
  };

  const openRemarks = (po) => {
    setRemarkPO(po);
    setRemarkText(po.accountRemarks || po.paymentRemarks || "");
  };

  const saveRemark = async () => {
    if (!remarkPO) return;
    try {
      setSavingRemark(true);
      const res = await api.patch(`/purchase-orders/${remarkPO._id}/remarks`, {
        remarks: remarkText,
        accountRemarks: remarkText,
      });

      if (res.data?.success) {
        swal("Success", "Account remark updated successfully", "success");
        setOrders((prev) =>
          prev.map((item) =>
            item._id === remarkPO._id
              ? {
                ...item,
                accountRemarks: remarkText,
                paymentRemarks: remarkText,
                accountRemarksHistory:
                  res.data.data?.accountRemarksHistory || item.accountRemarksHistory,
              }
              : item
          )
        );
        setRemarkPO(null);
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to update remarks", "error");
    } finally {
      setSavingRemark(false);
    }
  };

  const savePayment = async () => {
    if (!selectedPO) return;
    const total = Math.round(Number(selectedPO.totalAmount || 0) * 100) / 100;
    const nextPaid = Math.round(Number(paidAmount || 0) * 100) / 100;
    const addlAmt = Math.round(Number(additionalPaymentAmount || 0) * 100) / 100;
    const currentPaid = Math.round(Number(selectedPO.paidAmount || 0) * 100) / 100;
    const remaining = Math.max(Math.round((total - currentPaid) * 100) / 100, 0);

    if (nextPaid <= 0 && addlAmt <= 0) {
      return swal("Error", "Please enter a valid paid amount or additional payment amount", "error");
    }
    if (nextPaid > remaining + 0.5) {
      return swal("Error", "Paid amount cannot exceed the remaining balance", "error");
    }

    try {
      setSavingPayment(true);
      const res = await api.patch(`/purchase-orders/${selectedPO._id}/payment`, {
        paidAmount: nextPaid,
        paymentReference,
        paymentRemarks,
        additionalPaymentType,
        additionalPaymentAmount: addlAmt,
      });

      if (res.data?.success) {
        if (additionalReceiptFile) {
          try {
            const formData = new FormData();
            formData.append("receipt", additionalReceiptFile);
            const paymentId = res.data.payment?._id;
            const uploadQuery = paymentId
              ? `?paymentId=${paymentId}&receiptType=additional`
              : `?receiptType=additional`;
            await api.patch(`/purchase-orders/${selectedPO._id}/upload-receipt${uploadQuery}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (fileErr) {
            console.error("Additional receipt upload error:", fileErr);
          }
        }

        swal("Success", "Other PO Payment recorded successfully", "success");
        setSelectedPO(null);
        setAdditionalReceiptFile(null);
        loadOrders();
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to update payment", "error");
    } finally {
      setSavingPayment(false);
    }
  };

  // Filtered List
  const filteredOrders = useMemo(() => {
    return orders.filter((po) => {
      const q = searchQuery.toLowerCase().trim();
      const poNum = (po.poNumber || "").toLowerCase();
      const vName = (po.vendor?.name || po.vendor?.companyName || "").toLowerCase();
      const remarks = (po.remarks || po.accountRemarks || "").toLowerCase();
      const itemNames = (po.items || []).map((it) => it.itemName || "").join(" ").toLowerCase();

      const matchesSearch =
        !q ||
        poNum.includes(q) ||
        vName.includes(q) ||
        remarks.includes(q) ||
        itemNames.includes(q);

      const paid = Number(po.paidAmount || 0);
      const total = Number(po.totalAmount || 0);
      const paymentStatus =
        po.paymentStatus ||
        (paid >= total && total > 0
          ? "Paid"
          : paid > 0
            ? "Partially Paid"
            : "Pending");

      const matchesStatus =
        statusFilter === "All" || paymentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchQuery, statusFilter]);

  // Overall statistics
  const summary = useMemo(() => {
    let totalAmt = 0;
    let paidAmt = 0;
    let pendingCount = 0;
    let paidCount = 0;

    orders.forEach((po) => {
      const t = Number(po.totalAmount || 0);
      const p = Number(po.paidAmount || 0);
      totalAmt += t;
      paidAmt += p;
      if (p >= t && t > 0) {
        paidCount++;
      } else {
        pendingCount++;
      }
    });

    return {
      totalCount: orders.length,
      totalAmt,
      paidAmt,
      balanceAmt: Math.max(totalAmt - paidAmt, 0),
      pendingCount,
      paidCount,
    };
  }, [orders]);

  return (
    <Fragment>
      <PageTitle activeMenu="Other PO Payments" motherMenu="Accounts" />

      {/* ===== TOP SUMMARY STATS ===== */}
      <Row className="g-3 mb-3">
        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="p-3">
              <div className="text-muted small fw-semibold text-uppercase">Other POs Count</div>
              <h4 className="mb-0 fw-bold mt-1 text-primary">{summary.totalCount}</h4>
              <small className="text-muted">
                {summary.paidCount} Paid · {summary.pendingCount} Pending/Partial
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="p-3">
              <div className="text-muted small fw-semibold text-uppercase">Total PO Value</div>
              <h4 className="mb-0 fw-bold mt-1">{money(summary.totalAmt)}</h4>
              <small className="text-muted">Direct / Non-WO POs</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="p-3">
              <div className="text-muted small fw-semibold text-uppercase">Total Paid</div>
              <h4 className="mb-0 fw-bold mt-1 text-success">{money(summary.paidAmt)}</h4>
              <small className="text-success">Cleared payments</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="p-3">
              <div className="text-muted small fw-semibold text-uppercase">Total Balance Due</div>
              <h4 className={`mb-0 fw-bold mt-1 ${summary.balanceAmt > 0 ? "text-danger" : "text-muted"}`}>
                {money(summary.balanceAmt)}
              </h4>
              <small className="text-muted">Pending settlement</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== FILTER & SEARCH BAR ===== */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-center justify-content-between">
            <Col xs={12} md={6}>
              <Form.Control
                type="text"
                placeholder="Search by PO Number, Vendor Name, Item, Remarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All Payment Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Partially Paid">Partially Paid</option>
                <option value="Paid">Paid</option>
              </Form.Select>
            </Col>
            <Col xs={12} md="auto">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("All");
                }}
                title="Reset Filters"
              >
                <i className="fas fa-undo me-1" /> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE CARD ===== */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <Card.Title className="mb-0 fs-5 fw-bold text-dark">
              Other Purchase Order Payments
            </Card.Title>
            <small className="text-muted">
              Purchase orders not linked to any specific project or work order
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate("/accounts")}
          >
            <i className="fas fa-arrow-left me-1" /> Back to Accounts
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Loading other purchase orders...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover responsive className="table align-middle text-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>PO NUMBER &amp; DATE</th>
                    <th>VENDOR</th>
                    <th>TOTAL AMOUNT</th>
                    <th>PAID AMOUNT</th>
                    <th>BALANCE</th>
                    <th>STATUS</th>
                    <th>RECEIPTS</th>
                    <th>ADDL CHARGES</th>
                    <th>SIGNED COPY</th>
                    <th className="text-end" style={{ minWidth: "210px" }}>
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((po) => {
                    const total = Math.round(Number(po.totalAmount || 0) * 100) / 100;
                    const paid = Math.round(Number(po.paidAmount || 0) * 100) / 100;
                    const balance = Math.max(Math.round((total - paid) * 100) / 100, 0);

                    const paymentStatus =
                      po.paymentStatus ||
                      (paid >= total && total > 0
                        ? "Paid"
                        : paid > 0
                          ? "Partially Paid"
                          : "Pending");

                    const vendorName = po.vendor?.name || po.vendor?.companyName || "Vendor";
                    const dateDisplay = po.issueDate || po.createdAt;

                    return (
                      <tr key={po._id}>
                        <td>
                          <strong className="text-primary">{po.poNumber || "-"}</strong>
                          {dateDisplay && (
                            <div className="small text-muted">
                              {new Date(dateDisplay).toLocaleDateString("en-IN")}
                            </div>
                          )}
                          {(po.accountRemarks || po.paymentRemarks) && (
                            <div
                              className="small text-muted mt-1 p-1 px-2 rounded bg-light border text-truncate"
                              style={{ maxWidth: "210px", fontSize: "0.75rem", cursor: "pointer" }}
                              title={`Click to view/edit: ${po.accountRemarks || po.paymentRemarks}`}
                              onClick={() => openRemarks(po)}
                            >
                              <i className="far fa-comment-alt text-info me-1" />
                              {po.accountRemarks || po.paymentRemarks}
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="fw-semibold">{vendorName}</div>
                          {po.vendor?.mobile && (
                            <div className="small text-muted">{po.vendor.mobile}</div>
                          )}
                        </td>


                        <td className="fw-semibold">{money(total)}</td>
                        <td className="text-success fw-semibold">{money(paid)}</td>
                        <td className={balance > 0 ? "text-danger fw-semibold" : "text-muted"}>
                          {money(balance)}
                        </td>

                        <td>
                          <Badge bg={statusVariant(paymentStatus)}>{paymentStatus}</Badge>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1">
                            {getPaymentRows(po).map((payment, index) => (
                              <div key={payment._id || index} className="d-flex align-items-center gap-1">
                                <span className="small text-muted text-nowrap">
                                  P{index + 1} {money(payment.amount)}
                                </span>
                                <ReceiptActions
                                  id={po._id}
                                  type="purchase-orders"
                                  receipt={payment.receipt}
                                  query={payment.legacy ? "" : `?paymentId=${payment._id}`}
                                  canUpload={true}
                                  onUploadSuccess={loadOrders}
                                  title="Receipt"
                                />
                              </div>
                            ))}
                            {!getPaymentRows(po).length && <span className="text-muted">-</span>}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1">
                            {getPaymentRows(po)
                              .filter((p) => Number(p.additionalPaymentAmount) > 0 || p.additionalPaymentReceipt)
                              .map((payment, index) => (
                                <div key={payment._id || index} className="d-flex align-items-center gap-1">
                                  <span
                                    className="small text-muted text-nowrap"
                                    title={`${payment.additionalPaymentType || "Additional"}: ${money(payment.additionalPaymentAmount)}`}
                                  >
                                    <span
                                      className="badge bg-light text-dark border me-1"
                                      style={{ fontSize: "0.68rem", padding: "1px 4px" }}
                                    >
                                      {payment.additionalPaymentType || "Addl"}
                                    </span>
                                    {money(payment.additionalPaymentAmount)}
                                  </span>
                                  <ReceiptActions
                                    id={po._id}
                                    type="purchase-orders"
                                    receipt={payment.additionalPaymentReceipt}
                                    query={
                                      payment.legacy
                                        ? "?receiptType=additional"
                                        : `?paymentId=${payment._id}&receiptType=additional`
                                    }
                                    canUpload={true}
                                    onUploadSuccess={loadOrders}
                                    title="Additional Receipt"
                                  />
                                </div>
                              ))}
                            {!getPaymentRows(po).some(
                              (p) => Number(p.additionalPaymentAmount) > 0 || p.additionalPaymentReceipt
                            ) && <span className="text-muted">-</span>}
                          </div>
                        </td>

                        <td>
                          <SignedBillActions
                            id={po._id}
                            type="purchase-orders"
                            signedCopy={po.signedCopy}
                            canUpload={true}
                            onUploadSuccess={loadOrders}
                          />
                        </td>

                        <td className="text-end align-middle" style={{ minWidth: "210px" }}>
                          <div className="d-inline-flex align-items-center gap-2 justify-content-end">
                            {paymentStatus === "Paid" ? (
                              <span
                                className="btn btn-sm btn-outline-success disabled text-nowrap d-inline-flex align-items-center gap-1"
                                style={{
                                  opacity: 1,
                                  pointerEvents: "none",
                                  fontWeight: 600,
                                  backgroundColor: "#f0fdf4",
                                  borderColor: "#22c55e",
                                  color: "#16a34a",
                                }}
                              >
                                <i className="fas fa-check-circle text-success" />
                                <span>Paid</span>
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="text-nowrap d-inline-flex align-items-center gap-1"
                                onClick={() => openPayment(po)}
                              >
                                Review / Mark Paid
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant={po.accountRemarks || po.paymentRemarks ? "outline-info" : "outline-secondary"}
                              className="text-nowrap d-inline-flex align-items-center gap-1"
                              onClick={() => openRemarks(po)}
                              title={
                                po.accountRemarks || po.paymentRemarks
                                  ? `Remark: ${po.accountRemarks || po.paymentRemarks}`
                                  : "Add Remark"
                              }
                            >
                              <i className="far fa-comment-dots" />
                              <span>Remark</span>
                              {(po.accountRemarks || po.paymentRemarks) && (
                                <span
                                  className="badge bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                  style={{ width: "16px", height: "16px", fontSize: "0.62rem", padding: 0 }}
                                >
                                  ✓
                                </span>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-5">
                        No other purchase orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ===== RECORD PAYMENT MODAL ===== */}
      <Modal
        show={!!selectedPO}
        onHide={() => setSelectedPO(null)}
        centered
        scrollable
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom py-3 px-4 bg-light">
          <div>
            <Modal.Title className="fs-5 fw-bold mb-0">
              <i className="fas fa-file-invoice-dollar text-primary me-2" />
              Other Purchase Order Payment
            </Modal.Title>
            {selectedPO && (
              <div className="small text-muted mt-1">
                PO: <strong className="text-dark">{selectedPO.poNumber}</strong>
                <span className="mx-2">·</span>
                Vendor:{" "}
                <strong className="text-dark">
                  {selectedPO.vendor?.name || selectedPO.vendor?.companyName || "Vendor"}
                </strong>
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          {selectedPO && paymentPreview && (
            <>
              {/* Financial Summary Card */}
              <div className="bg-light p-3 rounded mb-4 border">
                <Row className="g-2 text-center">
                  <Col xs={4}>
                    <div className="small text-muted fw-semibold text-uppercase">Total Amount</div>
                    <div className="fs-6 fw-bold mt-1 text-dark">{money(paymentPreview.total)}</div>
                  </Col>
                  <Col xs={4} className="border-start border-end">
                    <div className="small text-muted fw-semibold text-uppercase">Already Paid</div>
                    <div className="fs-6 fw-bold mt-1 text-success">{money(paymentPreview.currentPaid)}</div>
                  </Col>
                  <Col xs={4}>
                    <div className="small text-muted fw-semibold text-uppercase">Remaining Balance</div>
                    <div className="fs-6 fw-bold mt-1 text-danger">{money(paymentPreview.remaining)}</div>
                  </Col>
                </Row>
              </div>

              {/* Payment Input Form */}
              <Form>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="fw-semibold mb-0">
                      Paid Amount (₹) <span className="text-danger">*</span>
                    </Form.Label>
                    {paymentPreview.remaining > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        onClick={() => setPaidAmount(String(paymentPreview.remaining))}
                      >
                        Pay Full Balance ({money(paymentPreview.remaining)})
                      </Button>
                    )}
                  </div>
                  <Form.Control
                    type="number"
                    step="1"
                    min="0"
                    max={paymentPreview.remaining}
                    placeholder="Enter amount paid"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Balance after this payment:{" "}
                    <strong>{money(paymentPreview.nextRemaining)}</strong>
                  </Form.Text>
                </div>

                <div className="mb-3">
                  <Form.Label className="fw-semibold">
                    Payment Reference / Transaction ID
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. UTR / NEFT / IMPS / Cheque No."
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <Form.Label className="fw-semibold">Payment Remarks / Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Additional notes about this payment..."
                    value={paymentRemarks}
                    onChange={(e) => setPaymentRemarks(e.target.value)}
                  />
                </div>

                {/* Additional Charges / Freight / Unloading */}
                <div className="border-top pt-3 mt-3">
                  <div className="fw-semibold text-dark mb-2">
                    <i className="fas fa-truck-loading me-1 text-secondary" /> Additional Charges (Optional)
                  </div>
                  <Row className="g-2 mb-2">
                    <Col md={6}>
                      <Form.Label className="small text-muted mb-1">Charge Type</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Freight, Loading, Packaging"
                        value={additionalPaymentType}
                        onChange={(e) => setAdditionalPaymentType(e.target.value)}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small text-muted mb-1">Charge Amount (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0.00"
                        value={additionalPaymentAmount}
                        onChange={(e) => setAdditionalPaymentAmount(e.target.value)}
                      />
                    </Col>
                  </Row>
                  <div className="mb-2">
                    <Form.Label className="small text-muted mb-1">Additional Charge Receipt (Image/PDF)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => setAdditionalReceiptFile(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top py-3 px-4">
          <Button variant="secondary" onClick={() => setSelectedPO(null)}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={savingPayment || (!Number(paidAmount) && !Number(additionalPaymentAmount))}
            onClick={savePayment}
          >
            {savingPayment ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving Payment...
              </>
            ) : (
              "Confirm & Save Payment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== REMARKS MODAL ===== */}
      <Modal show={!!remarkPO} onHide={() => setRemarkPO(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">
            Accounts Remark — {remarkPO?.poNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small">Remark / Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter accounts department remark for this purchase order..."
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
            />
          </Form.Group>

          {Array.isArray(remarkPO?.accountRemarksHistory) &&
            remarkPO.accountRemarksHistory.length > 0 && (
              <div>
                <div className="small fw-semibold text-muted mb-2">Remarks History</div>
                <div
                  className="border rounded p-2 bg-light"
                  style={{ maxHeight: "180px", overflowY: "auto" }}
                >
                  {remarkPO.accountRemarksHistory.map((item, idx) => (
                    <div key={idx} className="small border-bottom pb-1 mb-1">
                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.75rem" }}>
                        <strong>{item.addedByName || "Accounts"}</strong>
                        <span>{new Date(item.date || item.createdAt).toLocaleDateString("en-IN")}</span>
                      </div>
                      <div className="mt-1">{item.remark}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setRemarkPO(null)}>
            Close
          </Button>
          <Button variant="primary" size="sm" disabled={savingRemark} onClick={saveRemark}>
            {savingRemark ? "Saving..." : "Save Remark"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default OtherPurchaseOrderPaymentList;
